import React, {useState, useEffect, Fragment} from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useMutation } from 'react-query';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { QRCode } from 'react-qrcode-logo';

import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { CardActions } from '@mui/material';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import ListSubheader from '@mui/material/ListSubheader';
import CircularProgress from '@mui/material/CircularProgress';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PaymentsIcon from '@mui/icons-material/Payments';

import apiClient from '../../request/http-common';
import { setCards } from '../../request/Analysis/hitorySlice';

const steps = ['Enter card number', 'Review activation fee', 'Pay & upload proof'];

const formatCardNumber = (value) => {
  const digits = String(value || '').replace(/\D+/g, '').slice(0, 16);
  return digits.replace(/(.{4})/g, '$1 ').trim();
};

const ActivateCard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const addresses = useSelector((state) => state.coins.addresses) || [];
  const coinRates = useSelector((state) => state.coins.coinRates) || [];
  const availableCoins = ['USDT', 'USDTERC20', 'USDTTRC20', 'XMR', 'XLM', 'ETH', 'TRX', 'XRP', 'BTC', 'LTC', 'BCH', 'DOGE', 'DASH', 'BNB'];

  const formatPrice = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', currencySign: 'standard' }).format(amount || 0);

  const [activeStep, setActiveStep] = useState(0);
  const [cardNumber, setCardNumber] = useState('');
  const [error, setErros] = useState('');
  const [card, setCard] = useState(null);
  const [settings, setSettings] = useState(null);
  const [coin, setCoin] = useState('');
  const [address, setAddress] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNote, setPaymentNote] = useState('');
  const [proof, setProof] = useState(null);
  const [proofName, setProofName] = useState('');
  const [buttonText, setbuttonText] = useState(null);

  useEffect(() => {
    const preset = searchParams.get('number');
    if (preset) {
      setCardNumber(formatCardNumber(preset));
    }
  }, [searchParams]);

  const { isLoading: isLookingUp, mutate: lookupCard } = useMutation(
    async () => {
      return await apiClient.post(`/api/lookup-card-activation`, {
        card_number: cardNumber,
      });
    },
    {
      onSuccess: (res) => {
        if (res.data.status === 'success') {
          setCard(res.data.card);
          setSettings(res.data.settings);
          setAmount(res.data.settings?.fee_amount || '');
          setActiveStep(1);
          setErros('');
          const event2 = new Event('processed');
          window.dispatchEvent(event2);
        }
      },
      onError: (err) => {
        const event2 = new Event('processed');
        window.dispatchEvent(event2);
        if (err.response?.data?.errors) {
          setErros(err.response.data.errors);
        } else {
          let errorMessage = err.response?.data.message || err;
          localStorage.setItem('notification', JSON.stringify({message : `${errorMessage}`, 'type': 'error' }));
          const event = new Event('newMessage');
          window.dispatchEvent(event);
        }
      },
    }
  );

  const { isLoading: isSubmitting, mutate: submitPayment } = useMutation(
    async () => {
      const formData = new FormData();
      formData.append('card_id', card.id);
      formData.append('amount', amount);
      formData.append('coin', coin.id);
      formData.append('payment_reference', paymentReference);
      formData.append('payment_note', paymentNote);
      formData.append('payment_proof', proof);

      return await apiClient.post(`/api/submit-card-activation-payment`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
    {
      onSuccess: (res) => {
        localStorage.setItem('notification', JSON.stringify({message : `${res.data.message}`, 'type': 'success' }));
        const event = new Event('newMessage');
        window.dispatchEvent(event);
        const event2 = new Event('processed');
        window.dispatchEvent(event2);

        apiClient.get("/api/history").then((historyRes) => {
          if (historyRes.data.status === 'success') {
            dispatch(setCards(historyRes.data.cards));
          }
        }).finally(() => {
          setTimeout(() => {
            navigate('/cards');
          }, 1800);
        });
      },
      onError: (err) => {
        const event2 = new Event('processed');
        window.dispatchEvent(event2);
        if (err.response?.data?.errors) {
          setErros(err.response.data.errors);
        } else {
          let errorMessage = err.response?.data.message || err;
          localStorage.setItem('notification', JSON.stringify({message : `${errorMessage}`, 'type': 'error' }));
          const event = new Event('newMessage');
          window.dispatchEvent(event);
        }
      },
    }
  );

  const handleLookup = () => {
    const event3 = new Event('processing');
    window.dispatchEvent(event3);
    lookupCard();
  };

  const handleSubmitPayment = () => {
    if (!coin) {
      localStorage.setItem('notification', JSON.stringify({message : 'Please select a payment method', 'type': 'error' }));
      const event = new Event('newMessage');
      window.dispatchEvent(event);
      return;
    }
    if (!proof) {
      localStorage.setItem('notification', JSON.stringify({message : 'Please upload your payment proof', 'type': 'error' }));
      const event = new Event('newMessage');
      window.dispatchEvent(event);
      return;
    }
    const event3 = new Event('processing');
    window.dispatchEvent(event3);
    submitPayment();
  };

  return (
    <div style={{marginTop: '70px'}}>
      <Grid container px={2} mt={4} mb={4} justifyContent="center">
        <Grid item xs={12} md={8} lg={6}>
          <Card sx={{marginX: '5px', borderRadius: '9px'}}>
            <CardContent>
              <Typography mt={2} sx={{ fontWeight: 'bold', fontSize: 24, textAlign: 'center' }} gutterBottom variant="h6" component="div">
                ACTIVATE CARD
              </Typography>
              <Typography sx={{ textAlign: 'center', fontSize: 16, mb: 3 }} variant="subtitle1" gutterBottom>
                Enter the card number you want to activate, review the fee, then complete payment.
              </Typography>

              <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              {activeStep === 0 && (
                <Box>
                  <FormControl fullWidth={true} sx={{ minWidth: 50, marginBottom: 2 }}>
                    <InputLabel htmlFor="card_number">Card Number</InputLabel>
                    {(error !== '' && error.card_number) ?
                      <OutlinedInput
                        error
                        value={cardNumber}
                        onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                        id="card_number"
                        name="card_number"
                        type="text"
                        inputProps={{ inputMode: 'numeric', maxLength: 19 }}
                        label="Card Number"
                        placeholder="XXXX XXXX XXXX XXXX"
                      />
                    :
                      <OutlinedInput
                        value={cardNumber}
                        onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                        id="card_number"
                        name="card_number"
                        type="text"
                        inputProps={{ inputMode: 'numeric', maxLength: 19 }}
                        label="Card Number"
                        placeholder="XXXX XXXX XXXX XXXX"
                      />
                    }
                    <Typography color='error' sx={{ textAlign: 'left', fontSize: 16 }} variant="subtitle1" gutterBottom>
                      {error !== '' && error.card_number}
                    </Typography>
                  </FormControl>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Type the 16-digit number of the card you intend to activate.
                  </Typography>
                </Box>
              )}

              {activeStep === 1 && settings && card && (
                <Box>
                  <Alert severity="info" sx={{ mb: 2, whiteSpace: 'pre-wrap', alignItems: 'flex-start' }}>
                    <Typography sx={{ fontWeight: 'bold', mb: 1 }}>{settings.title}</Typography>
                    {settings.fee_message}
                  </Alert>

                  <Box sx={{ bgcolor: 'background.default', borderRadius: 2, p: 2, mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Card to activate</Typography>
                    <Typography variant="h6" sx={{ letterSpacing: 2 }}>
                      {card.number1} {card.number2} {card.number3} {card.number4}
                    </Typography>
                    <Typography variant="body2">{card.card_type}</Typography>
                  </Box>

                  <Typography sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: 28 }} color="primary">
                    {formatPrice(settings.fee_amount)}
                  </Typography>
                  <Typography sx={{ textAlign: 'center', mb: 2 }} variant="body2" color="text.secondary">
                    Activation fee payable
                  </Typography>
                </Box>
              )}

              {activeStep === 2 && (
                <Box>
                  {coin === '' ?
                    <Fragment>
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        Pay {formatPrice(amount || settings?.fee_amount)} using any method below, then upload your payment proof.
                      </Alert>
                      <List
                        sx={{ width: '100%', bgcolor: 'background.paper' }}
                        subheader={<ListSubheader sx={{zIndex: 0, textAlign: 'center', position: 'relative'}}>SELECT ANY PAYMENT METHOD BELOW</ListSubheader>}
                      >
                        {Array.isArray(addresses) && addresses.length > 0 ? addresses.map((wallet) => (
                          coinRates.map((rate) => (
                            (wallet.coin_name === rate.symbol && availableCoins.includes(rate.symbol)) &&
                            <Fragment key={`${wallet.id}-${rate.symbol}`}>
                              <ListItem alignItems="flex-start">
                                <ListItemAvatar>
                                  <Avatar
                                    alt={rate.symbol}
                                    src={`https://assets.coincap.io/assets/icons/${rate.symbol.toLowerCase()}@2x.png`}
                                  />
                                </ListItemAvatar>
                                <ListItemText primary={rate.symbol} />
                                <Button
                                  onClick={() => {
                                    setCoin({ ...rate, id: wallet.id });
                                    setAddress(wallet.address);
                                  }}
                                  size="small"
                                  color="primary"
                                  variant="contained"
                                >
                                  Pay
                                </Button>
                              </ListItem>
                              <Divider component="li" />
                            </Fragment>
                          ))
                        )) : (
                          <Typography sx={{ textAlign: 'center', py: 3 }} color="text.secondary">
                            Payment wallets are not available yet. Please try again shortly.
                          </Typography>
                        )}
                      </List>
                    </Fragment>
                  :
                    <Box>
                      <Typography my={2} sx={{textAlign: 'center'}} variant="body2" color="secondary">
                        Kindly send {coin.symbol} to the address below, then upload your payment proof.
                      </Typography>

                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <QRCode
                          qrStyle="dots"
                          value={address}
                          size={200}
                          logoImage={`https://assets.coincap.io/assets/icons/${coin.symbol.toLowerCase()}@2x.png`}
                        />
                      </Box>

                      <CopyToClipboard onCopy={() => {
                        setbuttonText('Address Copied');
                        setTimeout(() => {
                          setbuttonText(null);
                        }, 2000);
                      }} text={address}>
                        <FormControl fullWidth={true} sx={{ minWidth: 50, marginBottom: 2 }}>
                          <TextField
                            minRows={1}
                            multiline
                            disabled
                            value={address}
                            id="address"
                            name="address"
                            type="text"
                            label={buttonText !== null ? buttonText : `Click to copy ${coin.symbol} address`}
                          />
                        </FormControl>
                      </CopyToClipboard>

                      <FormControl fullWidth={true} sx={{ minWidth: 50, marginBottom: 2 }}>
                        <InputLabel htmlFor="amount">Amount (USD)</InputLabel>
                        <OutlinedInput
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          id="amount"
                          type="number"
                          name="amount"
                          label="Amount (USD)"
                        />
                      </FormControl>

                      <FormControl fullWidth={true} sx={{ minWidth: 50, marginBottom: 2 }}>
                        <InputLabel htmlFor="payment_reference">Transaction ID / Hash</InputLabel>
                        <OutlinedInput
                          value={paymentReference}
                          onChange={(e) => setPaymentReference(e.target.value)}
                          id="payment_reference"
                          type="text"
                          name="payment_reference"
                          label="Transaction ID / Hash"
                        />
                      </FormControl>

                      <FormControl fullWidth={true} sx={{ minWidth: 50, marginBottom: 2 }}>
                        <InputLabel htmlFor="payment_note">Payment note (optional)</InputLabel>
                        <OutlinedInput
                          value={paymentNote}
                          onChange={(e) => setPaymentNote(e.target.value)}
                          id="payment_note"
                          type="text"
                          name="payment_note"
                          label="Payment note (optional)"
                        />
                      </FormControl>

                      <Button variant="outlined" component="label" fullWidth sx={{ mb: 1 }}>
                        {proofName ? proofName : 'Upload payment proof (image or PDF)'}
                        <input
                          hidden
                          accept="image/*,.pdf"
                          type="file"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            setProof(file || null);
                            setProofName(file ? file.name : '');
                          }}
                        />
                      </Button>
                      {(error !== '' && error.payment_proof) &&
                        <Typography color='error' variant="subtitle1">{error.payment_proof}</Typography>
                      }

                      <Button onClick={() => setCoin('')} fullWidth sx={{ mt: 1 }} color="error" variant="text">
                        Choose another payment method
                      </Button>
                    </Box>
                  }
                </Box>
              )}
            </CardContent>

            <CardActions sx={{ px: 2, pb: 3 }}>
              {activeStep === 0 && (
                <Fragment>
                  <Button onClick={() => navigate('/cards')} fullWidth color="error" variant="contained">
                    Cancel
                  </Button>
                  <Button onClick={handleLookup} fullWidth color="primary" variant="contained" startIcon={<CreditCardIcon />}>
                    {isLookingUp ? 'Checking...' : 'Continue'}
                  </Button>
                </Fragment>
              )}

              {activeStep === 1 && (
                <Fragment>
                  <Button onClick={() => setActiveStep(0)} fullWidth color="error" variant="contained">
                    Back
                  </Button>
                  <Button onClick={() => setActiveStep(2)} fullWidth color="primary" variant="contained" startIcon={<PaymentsIcon />}>
                    Proceed to Payment
                  </Button>
                </Fragment>
              )}

              {activeStep === 2 && coin === '' && (
                <Button onClick={() => setActiveStep(1)} fullWidth color="error" variant="contained">
                  Back
                </Button>
              )}

              {activeStep === 2 && coin !== '' && (
                <Fragment>
                  <Button onClick={() => setActiveStep(1)} fullWidth color="error" variant="contained">
                    Back
                  </Button>
                  <Button onClick={handleSubmitPayment} fullWidth color="primary" variant="contained" startIcon={isSubmitting ? <CircularProgress size={16} /> : <LockOpenIcon />}>
                    {isSubmitting ? 'Submitting...' : 'Submit Payment'}
                  </Button>
                </Fragment>
              )}
            </CardActions>
          </Card>
        </Grid>
      </Grid>
    </div>
  )
}

export default ActivateCard
